/**
 * Config for "Telegram Groups" video (MuxVideoPlayerWithCTA).
 * Used by: /grupo-privado, videoCTAConfig telegram.
 *
 * PRICING STRUCTURE:
 * - HIGH_PRICE ($29.99): US, Europe, MX, CO, PE, PY, BO, AE + default
 * - LOW_PRICE: CL, BR, UY
 * - ARGENTINA: Uses env var or fallback
 *
 * VIDEO LANGUAGE LOGIC:
 * - Optional “all in one”: `VITE_MUX_CTA_UNIVERSAL_PLAYBACK_ID` (Mux ID or stream URL) when ES/EN/fallback envs are empty
 * - Spanish-speaking countries → Spanish video (VITE_TELEGRAM_GROUPS_PLAYBACK_ID)
 * - Europe (except ES) → English video (VITE_TELEGRAM_GROUPS_EN_PLAYBACK_ID)
 * - US, Middle East → English video
 * - Country-specific override if VITE_TELEGRAM_GROUPS_XX_PLAYBACK_ID exists
 *
 * CTA destino “uno para todos”: `VITE_TELEGRAM_GROUPS_UNIVERSAL_CTA_URL` (ej. t.me al grupo free)
 * **solo** si `VITE_MUX_CTA_FRIENDLY_BANNER=true` (promo friendly). Sin eso, mapa por país / Stripe.
 */

import { getMuxCtaUniversalPlaybackId } from "./muxCtaUniversalPlayback";
import { appendBrandReferenceId } from "./stripeReferenceId";

// =============================================================================
// DEBUG: Log raw env vars on module load (only in development)
// =============================================================================
if (import.meta.env.DEV) {
  console.log("[telegramGroupsVideo] Raw env vars:", {
    PLAYBACK_ID: import.meta.env.VITE_TELEGRAM_GROUPS_PLAYBACK_ID,
    EN_PLAYBACK_ID: import.meta.env.VITE_TELEGRAM_GROUPS_EN_PLAYBACK_ID,
    FALLBACK_PLAYBACK_ID: import.meta.env.VITE_TELEGRAM_GROUPS_FALLBACK_PLAYBACK_ID,
    MUX_CTA_UNIVERSAL: import.meta.env.VITE_MUX_CTA_UNIVERSAL_PLAYBACK_ID,
    UNIVERSAL_CTA_URL: import.meta.env.VITE_TELEGRAM_GROUPS_UNIVERSAL_CTA_URL,
    types: {
      PLAYBACK_ID: typeof import.meta.env.VITE_TELEGRAM_GROUPS_PLAYBACK_ID,
      EN_PLAYBACK_ID: typeof import.meta.env.VITE_TELEGRAM_GROUPS_EN_PLAYBACK_ID,
    }
  });
}

// =============================================================================
// STRIPE PAYMENT LINKS (configurable via env vars)
// =============================================================================

// Strict mode: no cross-brand fallback URLs.
// CTA links must come from Railway env vars; if absent, leave empty.
const HIGH_PRICE_URL = "";

// Low price - Chile, Brasil, Uruguay
const LOW_PRICE_URL = "";

// Argentina fallback disabled in strict mode
const ARGENTINA_FALLBACK_URL = "";

// Country-specific Stripe URLs (can be overridden by env vars)
const TELEGRAM_GROUPS_US_CTA_URL =
  (import.meta.env.VITE_TELEGRAM_GROUPS_US_CTA_URL as string) || HIGH_PRICE_URL;

const TELEGRAM_GROUPS_ES_CTA_URL =
  (import.meta.env.VITE_TELEGRAM_GROUPS_ES_CTA_URL as string) || HIGH_PRICE_URL;

const TELEGRAM_GROUPS_MX_CTA_URL =
  (import.meta.env.VITE_TELEGRAM_GROUPS_MX_CTA_URL as string) || HIGH_PRICE_URL;

const TELEGRAM_GROUPS_AE_CTA_URL =
  (import.meta.env.VITE_TELEGRAM_GROUPS_AE_CTA_URL as string) || HIGH_PRICE_URL;

const TELEGRAM_GROUPS_SA_CTA_URL =
  (import.meta.env.VITE_TELEGRAM_GROUPS_SA_CTA_URL as string) || HIGH_PRICE_URL;

// =============================================================================
// VIDEO PLAYBACK IDs
// =============================================================================

// Helper to get env var (returns empty string if not set or empty)
// Also validates that it's a valid Mux playback ID format
const getEnvVar = (envVar: string | undefined, varName?: string): string => {
  // Check if undefined or null first
  if (envVar === undefined || envVar === null) {
    if (varName && import.meta.env.DEV) {
      console.warn(`[telegramGroupsVideo] ${varName} is undefined or null`);
    }
    return "";
  }
  
  // Convert to string and trim
  const trimmed = String(envVar).trim();
  
  // Check if empty after trim
  if (trimmed.length === 0) {
    if (varName && import.meta.env.DEV) {
      console.warn(`[telegramGroupsVideo] ${varName} is empty string`);
    }
    return "";
  }
  
  // Validate it's not a placeholder value
  if (trimmed.includes("your-") || trimmed.includes("tu-") || trimmed.includes("example") || trimmed.includes("xxx")) {
    console.warn(`[telegramGroupsVideo] ${varName || 'Variable'} has placeholder value: "${trimmed.substring(0, 20)}..."`);
    return "";
  }
  
  // Validate minimum length for Mux playback IDs (typically 40+ chars)
  if (trimmed.length < 10) {
    console.warn(`[telegramGroupsVideo] ${varName || 'Variable'} too short (${trimmed.length} chars): "${trimmed}"`);
    return "";
  }
  
  // Success - only log in development
  if (varName && import.meta.env.DEV) {
    console.log(`[telegramGroupsVideo] ✅ ${varName} loaded: ${trimmed.substring(0, 20)}... (${trimmed.length} chars)`);
  }
  
  return trimmed;
};

// Strict mode: no hardcoded playback fallback.
export const HARDCODED_FALLBACK_PLAYBACK_ID = "";

/** Playback ID para Argentina; usado por /ar/invitacion. Prioridad: VITE_TELEGRAM_GROUPS_AR_PLAYBACK_ID > español > fallback. */
const AR_PLAYBACK_ID =
  getEnvVar(import.meta.env.VITE_TELEGRAM_GROUPS_AR_PLAYBACK_ID as string, "VITE_TELEGRAM_GROUPS_AR_PLAYBACK_ID");

/** Playback ID VIP para /ar/invitacion (opción VIP). Definir VITE_AR_INVITACION_VIP_PLAYBACK_ID en .env; si no, usa el estándar AR. */
const AR_VIP_PLAYBACK_ID =
  getEnvVar(import.meta.env.VITE_AR_INVITACION_VIP_PLAYBACK_ID as string, "VITE_AR_INVITACION_VIP_PLAYBACK_ID");

const MUX_CTA_UNIVERSAL_PLAYBACK_ID = getMuxCtaUniversalPlaybackId();

const FALLBACK_PLAYBACK_ID = "";

// Main playback ID (Spanish) - VITE_TELEGRAM_GROUPS_PLAYBACK_ID
const SPANISH_PLAYBACK_RAW =
  getEnvVar(import.meta.env.VITE_TELEGRAM_GROUPS_PLAYBACK_ID as string, "VITE_TELEGRAM_GROUPS_PLAYBACK_ID");
export const TELEGRAM_GROUPS_PLAYBACK_ID = SPANISH_PLAYBACK_RAW;

if (!SPANISH_PLAYBACK_RAW && import.meta.env.DEV) {
  console.warn(`[telegramGroupsVideo] ⚠️ VITE_TELEGRAM_GROUPS_PLAYBACK_ID not configured, using FALLBACK: ${FALLBACK_PLAYBACK_ID.substring(0, 20)}...`);
}

// English playback ID - VITE_TELEGRAM_GROUPS_EN_PLAYBACK_ID
const ENGLISH_PLAYBACK_RAW = getEnvVar(import.meta.env.VITE_TELEGRAM_GROUPS_EN_PLAYBACK_ID as string, "VITE_TELEGRAM_GROUPS_EN_PLAYBACK_ID");
export const TELEGRAM_GROUPS_EN_PLAYBACK_ID = ENGLISH_PLAYBACK_RAW;

if (!ENGLISH_PLAYBACK_RAW && import.meta.env.DEV) {
  console.warn(`[telegramGroupsVideo] ⚠️ VITE_TELEGRAM_GROUPS_EN_PLAYBACK_ID not configured, using ${SPANISH_PLAYBACK_RAW ? 'Spanish' : 'FALLBACK'}: ${TELEGRAM_GROUPS_EN_PLAYBACK_ID.substring(0, 20)}...`);
}

// =============================================================================
// COUNTRY/REGION DEFINITIONS
// =============================================================================

// Spanish-speaking countries (use Spanish video)
const SPANISH_SPEAKING_COUNTRIES = [
  "ES", // Spain
  "MX", "AR", "CO", "PE", "CL", "VE", "EC", "BO", "PY", "UY", // LATAM
  "CR", "PA", "DO", "GT", "HN", "NI", "SV", "CU", "PR", // Central America & Caribbean
  "LATAM", // Fallback cuando Worker no detecta país - video en español
];

// European countries (use English video, HIGH_PRICE)
const EUROPEAN_COUNTRIES = [
  "DE", "FR", "IT", "PT", "NL", "BE", "AT", "CH", "GB", "IE",
  "SE", "NO", "DK", "FI", "PL", "CZ", "GR", "RO", "HU", "SK",
  "BG", "HR", "SI", "LT", "LV", "EE", "LU", "MT", "CY", "IS", // Iceland
];

// Middle East countries (use English video, HIGH_PRICE)
const MIDDLE_EAST_COUNTRIES = ["AE", "SA", "QA", "KW", "BH", "OM"];

// Argentina CTA URL (only country with special env var override)
const TELEGRAM_GROUPS_AR_CTA_URL =
  (import.meta.env.VITE_TELEGRAM_GROUPS_AR_CTA_URL as string) || "";

/** Igual que en `videoCTAConfig` para el cartel friendly. */
function readMuxCtaFriendlyBannerEnvLocal(): "on" | "off" | "unset" {
  const raw = (import.meta.env.VITE_MUX_CTA_FRIENDLY_BANNER as string | undefined)
    ?.toLowerCase()
    .trim();
  if (raw === "true" || raw === "1" || raw === "yes" || raw === "on") return "on";
  if (raw === "false" || raw === "0" || raw === "no" || raw === "off")
    return "off";
  return "unset";
}

/** URL universal solo con promo friendly (`FRIENDLY_BANNER=true`). */
function shouldApplyUniversalTelegramCtaUrl(): boolean {
  return readMuxCtaFriendlyBannerEnvLocal() === "on";
}

/**
 * Parseo de `VITE_TELEGRAM_GROUPS_UNIVERSAL_CTA_URL` (sin gateo; el gate va aparte).
 */
function getTelegramGroupsUniversalCtaUrlRaw(): string {
  const raw = String(
    import.meta.env.VITE_TELEGRAM_GROUPS_UNIVERSAL_CTA_URL ?? "",
  ).trim();
  if (raw.length < 12) return "";
  if (/your-|tu-|example|xxx/i.test(raw)) return "";
  if (!/^https?:\/\//i.test(raw)) return "";
  return raw;
}

const TELEGRAM_GROUPS_UNIVERSAL_CTA_URL_RAW = getTelegramGroupsUniversalCtaUrlRaw();
const TELEGRAM_GROUPS_UNIVERSAL_CTA_FOR_FUNNEL =
  TELEGRAM_GROUPS_UNIVERSAL_CTA_URL_RAW && shouldApplyUniversalTelegramCtaUrl()
    ? TELEGRAM_GROUPS_UNIVERSAL_CTA_URL_RAW
    : "";

if (
  import.meta.env.DEV &&
  readMuxCtaFriendlyBannerEnvLocal() === "on" &&
  !TELEGRAM_GROUPS_UNIVERSAL_CTA_URL_RAW
) {
  console.warn(
    "[telegramGroupsVideo] VITE_MUX_CTA_FRIENDLY_BANNER=true pero falta VITE_TELEGRAM_GROUPS_UNIVERSAL_CTA_URL — el CTA sigue el mapa por país (Stripe / envs por país).",
  );
}

// Export for backwards compatibility (with juaperrone reference for tracking)
export const TELEGRAM_GROUPS_CTA_URL = appendBrandReferenceId(
  TELEGRAM_GROUPS_UNIVERSAL_CTA_FOR_FUNNEL || "",
);

/** True si hay playback para channel_invite (env o fallback AR). Usado por hasVideoConfigured. */
export function hasChannelInviteVideo(): boolean {
  return Boolean(
    TELEGRAM_GROUPS_PLAYBACK_ID ||
    TELEGRAM_GROUPS_EN_PLAYBACK_ID
  );
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Check if a country is Spanish-speaking
 */
export function isSpanishSpeakingCountry(countryCode: string): boolean {
  return SPANISH_SPEAKING_COUNTRIES.includes(countryCode.toUpperCase());
}

/**
 * Check if a country is European (except Spain which is Spanish-speaking)
 */
export function isEuropeanCountry(countryCode: string): boolean {
  return EUROPEAN_COUNTRIES.includes(countryCode.toUpperCase());
}

/**
 * Check if a country is in Middle East
 */
export function isMiddleEastCountry(countryCode: string): boolean {
  return MIDDLE_EAST_COUNTRIES.includes(countryCode.toUpperCase());
}

/**
 * Get playback ID by country without implicit fallback chains.
 * - Spanish-speaking → Spanish video, or English if Spanish not configured
 * - Europe, US, Middle East → English video
 * - GARANTIZA que siempre retorne un playbackId válido (nunca vacío)
 */
function getDefaultPlaybackIdForCountry(countryCode: string): string {
  const upperCode = countryCode.toUpperCase();
  
  // Argentina: playback específico para /ar/invitacion (VITE_TELEGRAM_GROUPS_AR_PLAYBACK_ID) si está definido
  if (upperCode === "AR" && AR_PLAYBACK_ID) {
    return AR_PLAYBACK_ID;
  }

  // Spanish-speaking countries: Spanish video only.
  if (isSpanishSpeakingCountry(upperCode)) {
    return TELEGRAM_GROUPS_PLAYBACK_ID || "";
  }
  
  // US, Europe, Middle East get English video only.
  if (upperCode === "US" || isEuropeanCountry(upperCode) || isMiddleEastCountry(upperCode)) {
    return TELEGRAM_GROUPS_EN_PLAYBACK_ID || "";
  }
  
  // Unknown countries: no implicit fallback playback.
  return "";
}

// =============================================================================
// MAIN CONFIGURATION FUNCTION
// =============================================================================

// Todos los países BioLinkTemplate + fallbacks
const TELEGRAM_PLAYBACK_COUNTRIES = [
  "US", "ES", "DE", "FR", "IT", "PT", "NL", "BE", "AT", "CH", "GB", "IE",
  "SE", "NO", "DK", "FI", "PL", "CZ", "GR", "RO", "HU", "SK", "BG", "HR",
  "SI", "LT", "LV", "EE", "LU", "MT", "CY", "IS", // Europe + Iceland
  "MX", "CO", "PE", "PY", "BO", "CL", "BR", "UY", "AR", "AE", "SA", "LATAM",
] as const;

/**
 * Get Telegram Groups video configuration for all countries.
 * Strict mode: if no playback is configured for a country, keep it empty.
 */
export function getTelegramGroupsVideoConfig() {
  const defaultId = "";
  const playbackIds: Record<string, string> = {
    default: defaultId,
  };
  for (const cc of TELEGRAM_PLAYBACK_COUNTRIES) {
    const id = getTelegramPlaybackIdByCountry(cc);
    playbackIds[cc] = id;
  }

  return {
    playbackIds,
    ctaUrls: {
      // HIGH PRICE ($29.99) - US
      US: TELEGRAM_GROUPS_US_CTA_URL,

      // HIGH PRICE ($29.99) - Spain (Spanish-speaking Europe)
      ES: TELEGRAM_GROUPS_ES_CTA_URL,

      // HIGH PRICE ($29.99) - Europe (English video)
      DE: HIGH_PRICE_URL,
      FR: HIGH_PRICE_URL,
      IT: HIGH_PRICE_URL,
      PT: HIGH_PRICE_URL,
      NL: HIGH_PRICE_URL,
      BE: HIGH_PRICE_URL,
      AT: HIGH_PRICE_URL,
      CH: HIGH_PRICE_URL,
      GB: HIGH_PRICE_URL,
      IE: HIGH_PRICE_URL,
      SE: HIGH_PRICE_URL,
      NO: HIGH_PRICE_URL,
      DK: HIGH_PRICE_URL,
      FI: HIGH_PRICE_URL,
      PL: HIGH_PRICE_URL,
      CZ: HIGH_PRICE_URL,
      GR: HIGH_PRICE_URL,
      RO: HIGH_PRICE_URL,
      HU: HIGH_PRICE_URL,
      SK: HIGH_PRICE_URL,
      BG: HIGH_PRICE_URL,
      HR: HIGH_PRICE_URL,
      SI: HIGH_PRICE_URL,
      LT: HIGH_PRICE_URL,
      LV: HIGH_PRICE_URL,
      EE: HIGH_PRICE_URL,
      LU: HIGH_PRICE_URL,
      MT: HIGH_PRICE_URL,
      CY: HIGH_PRICE_URL,
      IS: HIGH_PRICE_URL, // Iceland - BioLinkTemplate

      // HIGH PRICE ($29.99) - LATAM
      MX: TELEGRAM_GROUPS_MX_CTA_URL,
      CO: HIGH_PRICE_URL,
      PE: HIGH_PRICE_URL,
      PY: HIGH_PRICE_URL,
      BO: HIGH_PRICE_URL,

      // HIGH PRICE ($29.99) - Middle East
      AE: TELEGRAM_GROUPS_AE_CTA_URL,
      SA: TELEGRAM_GROUPS_SA_CTA_URL,

      // LOW PRICE - Chile, Brasil, Uruguay
      CL: LOW_PRICE_URL,
      BR: LOW_PRICE_URL,
      UY: LOW_PRICE_URL,

      // ARGENTINA - env var or fallback
      AR: TELEGRAM_GROUPS_AR_CTA_URL || ARGENTINA_FALLBACK_URL,

      // LATAM fallback (Worker sin país específico) - Spanish video, high price
      LATAM: "",

      // DEFAULT - High price for unlisted countries
      default: "",
    },
  };
}

/**
 * Get CTA URL for a specific country
 * Automatically appends client_reference_id=juaperrone for Stripe tracking
 */
export function getTelegramCTAUrlByCountry(countryCode: string): string {
  if (TELEGRAM_GROUPS_UNIVERSAL_CTA_FOR_FUNNEL) {
    return appendBrandReferenceId(TELEGRAM_GROUPS_UNIVERSAL_CTA_FOR_FUNNEL);
  }
  const config = getTelegramGroupsVideoConfig();
  const upperCode = countryCode.toUpperCase();

  let url: string;
  if (upperCode in config.ctaUrls) {
    url = config.ctaUrls[upperCode as keyof typeof config.ctaUrls];
  } else {
    url = config.ctaUrls.default;
  }

  // Append juaperrone reference ID for tracking
  return appendBrandReferenceId(url);
}

/**
 * Get playback ID for a specific country.
 *
 * Priority:
 * 1. ES (Spain): use default Spanish playback.
 * 2. Country-specific env var (VITE_TELEGRAM_GROUPS_XX_PLAYBACK_ID) for overrides.
 * 3. Language-based fallback (Spanish-speaking → Spanish, US/EU/ME → English).
 *
 * La visibilidad/geo se maneja en Cloudflare; el worker elige template por país.
 * Este config solo selecciona playback por idioma/región.
 * 
 * Strict mode: if there is no configured playback for that country, returns empty string.
 */
export function getTelegramPlaybackIdByCountry(countryCode: string): string {
  const upperCode = countryCode.toUpperCase();

  // Spain: use main Spanish video
  if (upperCode === "ES") {
    return getDefaultPlaybackIdForCountry(upperCode);
  }

  // Check for country-specific env var
  const countryEnvVar = getEnvVar(
    import.meta.env[`VITE_TELEGRAM_GROUPS_${upperCode}_PLAYBACK_ID`] as string
  );

  if (countryEnvVar) {
    console.log(`[telegramGroupsVideo] Using country-specific playback for ${upperCode}`);
    return countryEnvVar;
  }

  // Language/region mapping without fallback chain.
  return getDefaultPlaybackIdForCountry(upperCode);
}

/** Playback ID para /ar/invitacion (channel_invite). */
export function getChannelInvitePlaybackIdForAR(): string {
  return AR_PLAYBACK_ID || getTelegramPlaybackIdByCountry("AR") || "";
}

/** Playback ID para la opción VIP en /ar/invitacion. Si no está definido VITE_AR_INVITACION_VIP_PLAYBACK_ID, usa el estándar AR. */
export function getChannelInviteVipPlaybackIdForAR(): string {
  return AR_VIP_PLAYBACK_ID || getChannelInvitePlaybackIdForAR();
}

/**
 * Get playback ID directly by language code.
 * @param language - Language code like "es", "en", "es-AR", "en-US"
 */
export function getTelegramPlaybackIdByLanguage(language: string): string {
  const baseLang = language.split("-")[0].toLowerCase();
  
  if (baseLang === "es") {
    return TELEGRAM_GROUPS_PLAYBACK_ID;
  }
  
  // English and all other languages
  return TELEGRAM_GROUPS_EN_PLAYBACK_ID;
}
